import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtMoney, findCurrency } from "@/lib/api";
import { LOAN_DETAIL } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, FileCheck2, Loader2 } from "lucide-react";
import RegionContextCard from "@/components/RegionContextCard";

export default function LoanDetail() {
  const { industryId } = useParams();
  const [industry, setIndustry] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [iRes, cRes, rRes] = await Promise.all([
          api.get(`/industries/${industryId}`),
          api.get("/currencies"),
          api.get("/regions"),
        ]);
        setIndustry(iRes.data);
        setCurrencies(cRes.data.currencies);
        setRegions(rRes.data.regions);
      } finally {
        setLoading(false);
      }
    })();
  }, [industryId]);

  const region = regions.find((r) => r.id === regionId) || null;

  // When region changes, default the display currency to the region's default
  // (only if the user hasn't already explicitly picked a different currency).
  const onRegionChange = (id) => {
    setRegionId(id);
    const r = regions.find((x) => x.id === id);
    if (r?.default_currency) setCurrencyCode(r.default_currency);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading loan plan…
      </div>
    );
  }
  if (!industry) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <p>Industry not found.</p>
      </div>
    );
  }

  const currency = findCurrency(currencies, currencyCode);

  return (
    <div
      data-testid={LOAN_DETAIL.page(industry.id)}
      className="max-w-7xl mx-auto px-6 lg:px-10 py-14"
    >
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-3xl border border-border">
        <img
          src={industry.image_url}
          alt={industry.name}
          className="w-full h-64 lg:h-80 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10 text-white">
          <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold">
            Imali {industry.name} Business Loan
          </p>
          <h1 className="mt-3 font-display text-4xl lg:text-6xl font-light tracking-tighter">
            {industry.name} loan plan
          </h1>
          <p className="mt-2 max-w-2xl text-white/85">{industry.tagline}</p>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Main column */}
        <div className="lg:col-span-8 space-y-10">
          {/* Region & Currency selectors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-border pb-6">
            <div>
              <p className="text-xs tracking-[0.28em] uppercase text-muted-foreground font-semibold mb-2">
                Region / banking system
              </p>
              <Select value={regionId} onValueChange={onRegionChange}>
                <SelectTrigger
                  className="w-full rounded-full"
                  data-testid={LOAN_DETAIL.regionSelect}
                >
                  <SelectValue placeholder="Select a region (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs tracking-[0.28em] uppercase text-muted-foreground font-semibold mb-2">
                Display currency
              </p>
              <Select value={currencyCode} onValueChange={setCurrencyCode}>
                <SelectTrigger
                  className="w-full rounded-full"
                  data-testid="loan-detail-currency-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="mr-2">{c.flag}</span>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Key metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              data-testid={LOAN_DETAIL.amountRange}
              className="p-6 rounded-2xl border border-border bg-card"
            >
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                Loan amount
              </p>
              <p className="mt-4 font-mono text-2xl text-primary">
                {fmtMoney(industry.min_amount, currency)}
              </p>
              <p className="mt-1 font-mono text-sm text-muted-foreground">
                to {fmtMoney(industry.max_amount, currency)}
              </p>
            </div>
            <div
              data-testid={LOAN_DETAIL.tenureRange}
              className="p-6 rounded-2xl border border-border bg-card"
            >
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                Tenure
              </p>
              <p className="mt-4 font-mono text-2xl text-primary">
                {industry.min_tenure_months}–{industry.max_tenure_months}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">months</p>
            </div>
            <div
              data-testid={LOAN_DETAIL.rateRange}
              className="p-6 rounded-2xl border border-border bg-card"
            >
              <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
                Interest rate
              </p>
              <p className="mt-4 font-mono text-2xl text-primary">
                {industry.min_interest_rate}%–{industry.max_interest_rate}%
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                p.a. · Processing fee {industry.processing_fee_pct}%
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <h2 className="font-display text-2xl tracking-tight">About this plan</h2>
            <p className="mt-4 leading-relaxed text-foreground/85">
              {industry.description}
            </p>
          </div>

          {/* Eligibility */}
          <div>
            <h2 className="font-display text-2xl tracking-tight">
              Eligibility
            </h2>
            <ul className="mt-4 space-y-3">
              {industry.eligibility.map((e) => (
                <li key={e} className="flex items-start gap-3">
                  <CheckCircle2
                    aria-hidden="true"
                    className="w-5 h-5 text-primary shrink-0 mt-0.5"
                  />
                  <span className="text-foreground/85">{e}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Regional banking context */}
          {region && (
            <div>
              <RegionContextCard region={region} />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-6">
            <div className="p-6 rounded-2xl border border-border bg-card">
              <h3 className="font-display text-xl tracking-tight">
                Required documents
              </h3>
              <ul className="mt-4 space-y-3">
                {industry.required_documents.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm">
                    <FileCheck2
                      aria-hidden="true"
                      className="w-4 h-4 text-secondary shrink-0 mt-0.5"
                    />
                    <span className="text-foreground/85">{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-6 rounded-2xl border border-primary/20 bg-primary text-primary-foreground">
              <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold">
                Ready to apply?
              </p>
              <h3 className="mt-3 font-display text-2xl tracking-tight leading-tight">
                Generate a lender-ready proposal in minutes.
              </h3>
              <Link
                to={`/proposals/new?type=loan&industry=${industry.id}&currency=${currencyCode}${
                  regionId ? `&region=${regionId}` : ""
                }`}
              >
                <Button
                  data-testid={LOAN_DETAIL.generateCta}
                  size="lg"
                  className="mt-6 w-full rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12"
                >
                  Generate AI proposal
                </Button>
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
